import { isValidFigmaUrl } from '@animaapp/anima-sdk';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useState } from 'react';
import { DialogLogo } from './DialogLogo';
import { FigmaFormIcon } from './FigmaFormIcon';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import type { FigmaFrame } from '~/lib/figma';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';

interface FigmaFrameDialogProps {
  open: boolean;
  onSubmit: (
    result?: FigmaFrame & {
      url: string;
    },
  ) => Promise<void>;
}

export function FigmaFrameDialog({ open, onSubmit }: FigmaFrameDialogProps) {
  return (
    <Dialog open={open} onClose={() => onSubmit()} maxWidth={600}>
      {open && <FigmaFrameDialogInner onSubmit={onSubmit} />}
    </Dialog>
  );
}

interface FigmaFrameDialogInnerProps {
  onSubmit: FigmaFrameDialogProps['onSubmit'];
}

function FigmaFrameDialogInner({ onSubmit }: FigmaFrameDialogInnerProps) {
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validation = isValidFigmaUrl(url);
  const [isValid /* fileKey  */, , nodeId] = validation;

  // FIXME: can we handle empty nodeIds?
  const valid = isValid && !!nodeId;

  async function handleSubmit() {
    const [isValid, fileKey, nodeId] = validation;

    if (import.meta.env.DEV) {
      invariant(isValid);
    }

    setSubmitting(true);

    await onSubmit({ fileKey, nodeId, url });

    setSubmitting(false);
  }

  const canSubmit = url !== '' && valid && !submitting;

  const incorrectLink = url !== '' && !valid;

  return (
    <>
      <VisuallyHidden>
        <DialogTitle>Choose a figma frame</DialogTitle>
      </VisuallyHidden>
      <DialogDescription noPadding>
        <DialogLogo />
        <div className="px-8 pb-6 flex flex-col items-center w-full text-center">
          <h1 className="mb-2 font-semibold text-lg">Choose a Figma frame</h1>

          <span className="text-sm text-bolt-elements-textSecondary mb-6">
            Turn your design into a live web app. Generate, preview and
            <br /> edit code instantly.
          </span>

          <div className="flex flex-col gap-1 w-100% text-left">
            <label
              className={classNames(`px-2 py-2 border rounded-md`, {
                'border-bolt-elements-borderColorDanger': incorrectLink,
                'border-bolt-elements-borderColor focus-within:border-bolt-elements-borderColorActive': !incorrectLink,
              })}
            >
              <FigmaFormIcon />

              <input
                className="outline-none bg-transparent pl-2 w-90% light:placeholder:text-black/30 dark:placeholder:text-white/30"
                placeholder="Paste Figma URL here"
                type="text"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && canSubmit) {
                    handleSubmit();
                  }
                }}
              />
            </label>

            {incorrectLink && (
              <p className="min-h-6">
                <span className="text-bolt-elements-item-contentDanger text-sm">
                  Link is incorrect, please select a Figma frame url.
                </span>
              </p>
            )}
          </div>

          <div className="mt-4 flex text-sm text-bolt-elements-textSecondary mb-6 text-left border-bolt-elements-borderColor border-1px p-3 rounded-md w-100%">
            <div>
              <div className="p-10px mr-2 i-ph:question dark:text-white light:text-black"></div>
            </div>
            <div>
              <p className="font-semibold text-bolt-elements-textSecondary"> How to get a Figma frame url? </p>
              <p className="text-bolt-elements-textSecondary">
                Right click on a frame <span className="px-1px">&gt;</span> "Copy/Paste as"{' '}
                <span className="px-1px">&gt;</span> "Copy link to selection"
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <LegacyButton variant="primary" size="lg" disabled={!canSubmit} onClick={handleSubmit}>
              {submitting ? 'Importing Figma frame into Bolt' : 'Import Figma frame into Bolt'}
            </LegacyButton>
          </div>
        </div>
      </DialogDescription>
    </>
  );
}
